import { Popover, PopoverButtonProps, Portal } from "@headlessui/react";
import { withErrorBoundary } from "@sentry/react";
import { compact } from "lodash";
import {
  ElementType,
  ReactElement,
  Ref,
  cloneElement,
  forwardRef,
  isValidElement,
  useState,
} from "react";
import { RiTeamLine } from "react-icons/ri";
import { usePopper } from "react-popper";

import Avatar from "@components/avatar/avatar";
import Button, {
  ButtonInSelectProps,
  ButtonProps,
  ButtonSize,
} from "@components/button/button";
import { ComboboxGenericClearButton } from "@components/combobox/generic-combobox";
import ErrorFallback from "@components/error-fallback/error-fallback";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

import UserComboboxList, {
  UserComboboxOption,
  UserComboboxOptionType,
} from "./user-combobox-list";

export type UserComboboxButtonProps = Omit<ButtonProps, "to" | "url"> & {
  ref: Ref<HTMLButtonElement>;
};

const defaultButtonProps = {
  size: ButtonSize.large,
  showDownChevronIcon: true,
};

const UserComboboxButton = forwardRef<
  HTMLButtonElement,
  UserComboboxButtonProps
>((props, ref) => {
  return <Popover.Button as={Button} {...props} ref={ref} />;
});
UserComboboxButton.displayName = "UserComboboxButtonProps";

export type UserComboboxChildrenProps = {
  value: UserComboboxOption | null;
  setReferenceElement?: (element: HTMLButtonElement) => void;
  clearable: boolean;
  disabled?: boolean;
  onClearValue?: () => void;
  onClickButton?: () => void;
};

const InternalUserCombobox = ({
  disabled = false,
  value,
  options = [],
  loading = false,
  query = "",
  clearable = false,
  onClearValue,
  onChangeValue,
  onChangeQuery,
  onClickButton,
  showEmail = false,
  searchPlaceholder = "Search employees...",
  placeholder = "Pick an employee",
  className = "",
  containerClassName,
  showUserActiveAssessmentNominationCount = false,
  children,
  buttonProps = defaultButtonProps,
  ...props
}: Omit<PopoverButtonProps<ElementType>, "children"> & {
  disabled?: boolean;
  value: UserComboboxOption | null;
  options: UserComboboxOption[];
  loading?: boolean;
  query: string;
  clearable?: boolean;
  onClearValue?: () => void;
  onChangeValue: (value: UserComboboxOption) => void;
  onChangeQuery: (query: string) => void;
  onClickButton?: () => void;
  showEmail?: boolean;
  searchPlaceholder?: string;
  placeholder?: string;
  containerClassName?: string;
  buttonProps?: ButtonInSelectProps;
  children?:
    | ReactElement<UserComboboxButtonProps>
    | React.FC<UserComboboxChildrenProps>;
  showUserActiveAssessmentNominationCount?: boolean;
}) => {
  const [referenceElement, setReferenceElement] = useState<
    HTMLButtonElement | HTMLDivElement | null
  >(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "bottom-start",
  });
  const filteredOptions =
    query === ""
      ? options
      : options.filter(
          (option) =>
            (option.type === UserComboboxOptionType.USER &&
              (option.name.toLowerCase().includes(query.toLowerCase()) ||
                option.email?.toLowerCase().includes(query.toLowerCase()))) ||
            (option.type === UserComboboxOptionType.TEAM &&
              option.title.toLowerCase().includes(query.toLowerCase())) ||
            (option.type === UserComboboxOptionType.ORG &&
              option.name.toLowerCase().includes(query.toLowerCase())) ||
            (option.type === UserComboboxOptionType.DIRECT_REPORTS &&
              "My Direct Reports"
                .toLowerCase()
                .includes(query.toLowerCase())) ||
            (option.type === UserComboboxOptionType.REPORTING_TREE &&
              "My Reporting Tree".toLowerCase().includes(query.toLowerCase()))
        );
  const validOptions = compact(filteredOptions);

  const handleChange = (close: () => void) => (option: UserComboboxOption) => {
    close();
    onChangeQuery("");
    onChangeValue(option);
  };

  const popoverButtonLabel = value
    ? value.type === UserComboboxOptionType.USER ||
      value.type === UserComboboxOptionType.ORG
      ? value.name
      : value.type === UserComboboxOptionType.DIRECT_REPORTS
      ? "My Direct Reports"
      : value.type === UserComboboxOptionType.REPORTING_TREE
      ? "My Reporting Tree"
      : value.title
    : null;

  // popover width is the width of the input, but has a minimum width
  const minWidth = 230;
  const inputWidth = referenceElement?.getBoundingClientRect().width || 0;
  const popoverWidth = Math.max(inputWidth, minWidth);

  const mergedButtonProps: ButtonInSelectProps = {
    ...defaultButtonProps,
    ...buttonProps,
  };

  if (children !== undefined && typeof children !== "function") {
    if (!isValidElement(children)) {
      throw new Error("Children must be a valid element");
    }
    if (children.type !== UserComboboxButton) {
      throw new Error("Children must be a UserComboboxButton");
    }
  }

  const showClearButton = clearable && !!onClearValue && value !== null;
  return (
    <Popover
      className={classNames("relative flex", containerClassName)}
      {...props}
    >
      {children &&
      isValidElement(children) &&
      children.type === UserComboboxButton ? (
        cloneElement(children, {
          ref: setReferenceElement,
          onClick: onClickButton,
        })
      ) : children && typeof children === "function" ? (
        children({
          clearable,
          onClearValue,
          value,
          onClickButton,
          setReferenceElement,
          disabled,
        })
      ) : (
        <Popover.Button
          as={Button}
          className={classNames("flex-1 text-left w-full", className)}
          onClick={onClickButton}
          {...mergedButtonProps}
          showDownChevronIcon={!showClearButton}
          ref={setReferenceElement}
          disabled={disabled}
        >
          {value ? (
            value.type === UserComboboxOptionType.USER ? (
              <Avatar user={value} size={4} />
            ) : value.type === UserComboboxOptionType.ORG ? (
              <TopicflowIcon type="organization" className="h-4 w-4" />
            ) : (
              <RiTeamLine className="h-4 w-4" />
            )
          ) : null}
          <span
            className={classNames(
              "flex-1 truncate",
              !popoverButtonLabel && "text-gray-400 font-normal"
            )}
          >
            {popoverButtonLabel ?? placeholder}
          </span>
          {clearable && onClearValue && value !== null && (
            <ComboboxGenericClearButton onClearValue={onClearValue} />
          )}
        </Popover.Button>
      )}

      <Portal key={"user-combobox"}>
        <Popover.Panel
          aria-label="User combobox popover"
          className={classNames(
            "absolute z-dropdown bg-white border rounded-md shadow-md"
          )}
          ref={setPopperElement}
          style={{
            ...styles.popper,
            width: `${popoverWidth}px`,
          }}
          {...attributes.popper}
        >
          {({ close, open }) => (
            <UserComboboxList
              close={close}
              open={open}
              onChange={handleChange}
              onChangeQuery={onChangeQuery}
              placeholder={searchPlaceholder}
              loading={loading}
              validOptions={validOptions}
              showEmail={showEmail}
              value={value}
              query={query}
              showUserActiveAssessmentNominationCount={
                showUserActiveAssessmentNominationCount
              }
            />
          )}
        </Popover.Panel>
      </Portal>
    </Popover>
  );
};

InternalUserCombobox.Button = UserComboboxButton;

const UserCombobox = withErrorBoundary(InternalUserCombobox, {
  fallback: ErrorFallback,
});

(UserCombobox as any).Button = UserComboboxButton;
UserCombobox.displayName = "UserComboboxButton";

export default UserCombobox as typeof InternalUserCombobox;
